import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizations } from '../hooks/useOrganizations';
import { IconChevronDown, IconBuilding } from '@tabler/icons-react';

export default function OrgSelector() {
    const { user, setSelectedOrg } = useAuth();
    const { data: organizationsData } = useOrganizations();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const organizations = organizationsData?.results || organizationsData || [];
    const selectedOrg = organizations.find(org => org.id === user?.orgId);
    const displayName = selectedOrg?.name || user?.organization?.name || 'Organization';

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // If only one org, just show a static badge
    if (organizations.length <= 1) {
        return (
            <div className="flex items-center gap-2 px-3 py-2 bg-white/10 border border-white/20 rounded-lg">
                <IconBuilding size={18} className="text-white" />
                <span className="text-sm font-semibold text-white">{displayName}</span>
            </div>
        );
    }

    const handleSelectOrg = (orgId) => {
        setSelectedOrg(orgId);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-all group"
            >
                <IconBuilding size={18} className="text-white group-hover:text-white/80" />
                <span className="text-sm font-medium text-white">
                    {displayName}
                </span>
                <IconChevronDown size={16} className="text-white group-hover:text-white/80 transition-transform" />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                    <div className="p-2">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                            Select Organization
                        </div>
                        {organizations.length === 0 ? (
                            <div className="px-3 py-4 text-sm text-gray-500 text-center">
                                No organizations found
                            </div>
                        ) : (
                            <>
                                {organizations.map((org) => (
                                    <button
                                        key={org.id}
                                        onClick={() => handleSelectOrg(org.id)}
                                        className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-100 transition-colors ${org.id === user?.orgId ? 'bg-primary/10 text-primary font-medium' : 'text-gray-700'
                                            }`}
                                    >
                                        <div className="font-medium">{org.name}</div>
                                        {org.code && (
                                            <div className="text-xs text-gray-500 mt-0.5">{org.code}</div>
                                        )}
                                    </button>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
